import { useState } from 'react'
import { Undo2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { ConfirmationDialog } from '@/components/common/ConfirmationDialog'
import type { WavePlanDetail, WavePlanUndoEntry } from '@/api/wavePlans'
import { useReturnFocus } from '@/hooks/useReturnFocus'
import {
  useUndoWavePlan,
  useWavePlanMutating,
  useWavePlanUndoStack,
} from '@/hooks/useWavePlans'

/**
 * Undo of the user's last edit of the plan, like the AP's: the tooltip names
 * the edit, a confirmation dialog asks first, and there is no redo. It waits
 * for running edits and a freshly loaded history, so the dialog names the edit
 * that is undone; the request carries that edit's id.
 */
export function WavePlanUndoButton({ detail }: { detail: WavePlanDetail }) {
  const canEdit = detail.permissions.can_edit
  const stack = useWavePlanUndoStack(detail.id, canEdit)
  const undo = useUndoWavePlan(detail.id)
  const mutating = useWavePlanMutating(detail.id)
  const focus = useReturnFocus()
  const [confirming, setConfirming] = useState<WavePlanUndoEntry | null>(null)
  const top = stack.data?.entries[0]
  const enabled = canEdit && !!top && stack.fresh && !mutating
  return (
    <>
      <Button
        size="sm"
        variant="outline"
        disabled={!enabled}
        title={top?.description ?? 'Nothing to undo'}
        onClick={() => {
          if (!top) return
          focus.remember()
          setConfirming(top)
        }}
      >
        <Undo2 size={14} /> Undo
      </Button>
      <ConfirmationDialog
        open={!!confirming}
        onOpenChange={open => !open && setConfirming(null)}
        title="Undo Last Change"
        description={`Are you sure you want to undo: "${confirming?.description}"? This cannot be redone.`}
        confirmText="Undo"
        cancelText="Cancel"
        variant="destructive"
        isLoading={undo.isPending}
        onCloseAutoFocus={focus.restore}
        onConfirm={() => {
          if (!confirming) return
          undo.mutate(confirming.id, { onSettled: () => setConfirming(null) })
        }}
      />
    </>
  )
}
